/**
 * AccountsTab — CON-01: Plan de Cuentas
 */
import { useState, useMemo, useEffect } from 'react'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { Plus, Loader2, Pencil, Trash2, RotateCcw, ToggleLeft, ToggleRight, BookOpen, Upload, Search, Lock, History } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import {
  useAccounts, useCreateAccount, useUpdateAccount, useDeleteAccount, useRestoreAccount,
  useAccountNameHistory,
  useImportAccountsCSV, useSIIAccounts,
  type ChartOfAccount, type AccountType, type SIIAccount,
  type CashFlowCategory, CASH_FLOW_CATEGORY_LABELS,
  type ExpenseCategory, EXPENSE_CATEGORY_LABELS,
  type BalanceSection, BALANCE_SECTION_LABELS,
} from '../hooks/useAccounting'
import { useTenantCurrency, useForeignCurrencies } from '../../../lib/locale'
import { ImportCSVMapperModal, type SystemField } from '@/components/ImportCSVMapperModal'
import { usePermission } from '@/hooks/usePermission'
import { Modal } from '@/components/ds/Modal'

const ACCOUNT_FIELDS: SystemField[] = [
  { key: 'code', label: 'Código', required: true, aliases: ['codigo', 'code'] },
  { key: 'name', label: 'Nombre', required: true, aliases: ['nombre', 'name'] },
  { key: 'description', label: 'Descripción', aliases: ['descripcion', 'description'] },
  { key: 'account_type', label: 'Tipo de Cuenta', required: true, aliases: ['tipo', 'account_type'] },
  { key: 'sii_code', label: 'Código Cuenta SII', aliases: ['codigo_sii', 'sii_code'] },
  { key: 'parent_code', label: 'Código Padre', aliases: ['codigo_padre', 'parent_code'] },
  { key: 'is_imputable', label: 'Imputable', aliases: ['imputable', 'is_imputable'] },
  { key: 'is_active', label: 'Activo', aliases: ['activo', 'is_active'] },
]

const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  asset:     'Activo',
  liability: 'Pasivo',
  equity:    'Patrimonio',
  revenue:   'Ingresos',
  expense:   'Gastos',
  other:     'Otro',
}

const ACCOUNT_TYPE_COLORS: Record<AccountType, string> = {
  asset:     'bg-primary-50 text-primary-hover',
  liability: 'bg-danger-surface text-danger-content',
  equity:    'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  revenue:   'bg-success-surface text-success-content',
  expense:   'bg-warning-surface text-warning-content',
  other:     'bg-surface-muted text-content-muted',
}

function NameHistoryModal({ accountId, onClose }: { accountId: string; onClose: () => void }) {
  const { data, isLoading } = useAccountNameHistory(accountId)
  return (
    <Modal open onClose={onClose} title="Historial de nombres" size="md">
      <div>
        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
        ) : !data || data.count === 0 ? (
          <p className="text-sm text-content-muted text-center py-6">
            Esta cuenta no ha tenido cambios de nombre.
          </p>
        ) : (
          <>
            <div className="mb-3 text-xs text-content-muted">
              Cuenta <span className="font-mono text-content">{data.account_code}</span> ·
              actual: <span className="text-content">{data.current_name}</span>
            </div>
            <ul className="space-y-2">
              {data.history.map(h => (
                <li key={h.id} className="border border-divider rounded-lg px-3 py-2 text-sm">
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-content-muted line-through">{h.old_name}</span>
                    <span className="text-content-subtle">→</span>
                    <span className="text-content font-medium">{h.new_name}</span>
                  </div>
                  <div className="mt-1 text-xs text-content-subtle">
                    {new Date(h.changed_at).toLocaleString()}
                    {h.changed_by ? ` · ${h.changed_by}` : ''}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Modal>
  )
}

interface AccountFormProps {
  account?: ChartOfAccount | null
  accounts: ChartOfAccount[]
  siiAccounts: SIIAccount[]
  onClose: () => void
}

function AccountForm({ account, accounts, siiAccounts, onClose }: AccountFormProps) {
  // La moneda base del tenant, que NO siempre es CLP: un tenant peruano lleva su
  // contabilidad en PEN. Y las extranjeras se ofrecen por país — la UF es chilena.
  const monedaBase = useTenantCurrency()
  const monedasExtranjeras = useForeignCurrencies()
  const isEdit = !!account
  const codeLocked = !!(account?.has_movements)
  const [showHistory, setShowHistory] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const [code, setCode] = useState(account?.code ?? '')
  const [name, setName] = useState(account?.name ?? '')
  const [description, setDescription] = useState(account?.description ?? '')
  const [accountType, setAccountType] = useState<AccountType>(account?.account_type ?? 'asset')
  const [siiCode, setSiiCode] = useState(account?.sii_code ?? '')
  const [siiSearch, setSiiSearch] = useState('')
  const [showSiiDropdown, setShowSiiDropdown] = useState(false)
  const [parent, setParent] = useState(account?.parent ?? '')
  const [isImputable, setIsImputable] = useState(account?.is_imputable ?? true)
  const [accountCurrency, setAccountCurrency] = useState(account?.account_currency ?? '')
  const [cashFlowCategory, setCashFlowCategory] = useState<CashFlowCategory>(account?.cash_flow_category ?? 'sin_clasificar')
  const [expenseCategory, setExpenseCategory] = useState<ExpenseCategory>(account?.expense_category ?? '')
  const [balanceSection, setBalanceSection] = useState<BalanceSection>(account?.balance_section ?? '')
  const [error, setError] = useState<string | null>(null)

  const create = useCreateAccount()
  const update = useUpdateAccount(account?.id ?? '')
  const isPending = create.isPending || update.isPending

  const filteredSii = useMemo(() => {
    if (!siiSearch) return siiAccounts.filter(a => a.is_active).slice(0, 20)
    const q = siiSearch.toLowerCase()
    return siiAccounts
      .filter(a => a.is_active && (a.code.toLowerCase().includes(q) || a.name.toLowerCase().includes(q)))
      .slice(0, 20)
  }, [siiSearch, siiAccounts])

  const selectedSiiName = useMemo(() => {
    if (!siiCode) return ''
    const found = siiAccounts.find(a => a.code === siiCode)
    return found ? `${found.code} - ${found.name}` : siiCode
  }, [siiCode, siiAccounts])

  const handleSave = () => {
    if (!code || !name) return
    setError(null)
    const payload = {
      code, name, description, account_type: accountType,
      sii_code: siiCode,
      parent: parent || null,
      is_imputable: isImputable,
      account_currency: accountCurrency,
      cash_flow_category: cashFlowCategory,
      expense_category: expenseCategory,
      balance_section: balanceSection,
    }
    const cb = {
      onSuccess: onClose,
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string; details?: Record<string, string[]> } } } }
        const details = err.response?.data?.error?.details
        if (details && typeof details === 'object') {
          const firstField = Object.keys(details)[0]
          const firstMsg = Array.isArray(details[firstField]) ? details[firstField][0] : null
          if (firstMsg) {
            setError(firstMsg)
            return
          }
        }
        setError(err.response?.data?.error?.message ?? 'Error al guardar')
      },
    }
    if (isEdit) {
      update.mutate(payload, cb)
    } else {
      create.mutate(payload, cb)
    }
  }

  const groupAccounts = accounts.filter(a => !a.is_imputable && a.is_active && a.id !== account?.id)

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar Cuenta' : 'Nueva Cuenta'} size="lg"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={isPending || !code || !name}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
        >
          {isPending && <Loader2 size={14} className="animate-spin" />}
          {isEdit ? 'Guardar Cambios' : 'Crear Cuenta'}
        </button>
      </>}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="accountstab-codigo" className="block text-xs font-medium text-content-muted mb-1 flex items-center gap-1">
              Código *
              {codeLocked && (
                <span title="El código no se puede cambiar porque la cuenta ya tiene movimientos contables">
                  <Lock size={12} className="text-content-subtle" />
                </span>
              )}
            </label>
            <input id="accountstab-codigo"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="ej. 1101"
              disabled={codeLocked}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary disabled:bg-surface-muted disabled:text-content-muted disabled:cursor-not-allowed"
            />
            {codeLocked && (
              <p className="mt-1 text-xs text-content-subtle">
                Para corregir un código se debe desactivar esta cuenta y crear una nueva.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="accountstab-tipo" className="block text-xs font-medium text-content-muted mb-1 flex items-center gap-1">
              Tipo *
              {codeLocked && (
                <span title="El tipo no se puede cambiar porque la cuenta ya tiene movimientos contables">
                  <Lock size={12} className="text-content-subtle" />
                </span>
              )}
            </label>
            {/* Con movimientos, cambiar el tipo movería saldos ya registrados de una
                sección a otra del balance y del EERR (el backend también lo rechaza). */}
            <select id="accountstab-tipo"
              value={accountType}
              onChange={e => setAccountType(e.target.value as AccountType)}
              disabled={codeLocked}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary disabled:bg-surface-muted disabled:text-content-muted disabled:cursor-not-allowed"
            >
              {Object.entries(ACCOUNT_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="accountstab-nombre" className="block text-xs font-medium text-content-muted">Nombre *</label>
            {isEdit && account && (
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                className="text-xs text-primary hover:text-primary-hover flex items-center gap-1"
              >
                <History size={12} />
                Ver historial
              </button>
            )}
          </div>
          <input id="accountstab-nombre"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="ej. Caja Chica"
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label htmlFor="accountstab-descripcion" className="block text-xs font-medium text-content-muted mb-1">Descripción</label>
          <textarea id="accountstab-descripcion"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Descripción de la cuenta (opcional)"
            rows={2}
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary resize-none"
          />
        </div>
        <div>
          <label htmlFor="accountstab-moneda-de-la-cuenta" className="block text-xs font-medium text-content-muted mb-1">Moneda de la Cuenta</label>
          <select id="accountstab-moneda-de-la-cuenta"
            value={accountCurrency}
            onChange={e => setAccountCurrency(e.target.value)}
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          >
            <option value="">{monedaBase} (por defecto)</option>
            {monedasExtranjeras.map(([codigo, etiqueta]) => (
              <option key={codigo} value={codigo}>{etiqueta}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-content-subtle">
            Aplica solo a cuentas bancarias en moneda extranjera (ej. cuenta corriente en USD).
          </p>
        </div>
        <div>
          <label htmlFor="accountstab-clasificacion-flujo-de-efectivo-nic-7" className="block text-xs font-medium text-content-muted mb-1">Clasificación Flujo de Efectivo (NIC 7)</label>
          <select id="accountstab-clasificacion-flujo-de-efectivo-nic-7"
            value={cashFlowCategory}
            onChange={e => setCashFlowCategory(e.target.value as CashFlowCategory)}
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          >
            {(Object.keys(CASH_FLOW_CATEGORY_LABELS) as CashFlowCategory[]).map(k => (
              <option key={k} value={k}>{CASH_FLOW_CATEGORY_LABELS[k]}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-content-subtle">
            Actividad de la cuenta en el Estado de Flujo de Efectivo. Marca como "Efectivo y
            equivalentes" las cuentas de caja/banco.
          </p>
        </div>
        {/* Solo tiene sentido en cuentas de gasto: es dónde se muestran en el EERR. */}
        {accountType === 'expense' && (
          <div>
            <label htmlFor="accountstab-seccion-en-el-estado-de-resultados" className="block text-xs font-medium text-content-muted mb-1">
              Sección en el Estado de Resultados
            </label>
            <select id="accountstab-seccion-en-el-estado-de-resultados"
              value={expenseCategory}
              onChange={e => setExpenseCategory(e.target.value as ExpenseCategory)}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            >
              {(Object.keys(EXPENSE_CATEGORY_LABELS) as ExpenseCategory[]).map(k => (
                <option key={k} value={k}>{EXPENSE_CATEGORY_LABELS[k]}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-content-subtle">
              En automático se deduce del código de la cuenta, lo que funciona con la numeración
              chilena (41 costo, 42/43 administración, 44/45 financieros). Con otro plan de
              cuentas hay que indicarlo acá, o el gasto se muestra en "otros".
            </p>
          </div>
        )}
        {/* Solo activos y pasivos: el patrimonio no se divide en corriente y no
            corriente, y los ingresos y gastos no van al balance. */}
        {(accountType === 'asset' || accountType === 'liability') && (
          <div>
            <label htmlFor="accountstab-seccion-en-el-balance-clasificado" className="block text-xs font-medium text-content-muted mb-1">
              Sección en el Balance Clasificado
            </label>
            <select id="accountstab-seccion-en-el-balance-clasificado"
              value={balanceSection}
              onChange={e => setBalanceSection(e.target.value as BalanceSection)}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            >
              {(Object.keys(BALANCE_SECTION_LABELS) as BalanceSection[]).map(k => (
                <option key={k} value={k}>{BALANCE_SECTION_LABELS[k]}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-content-subtle">
              En automático se deduce del código, lo que funciona con la numeración chilena
              (11 activo corriente, 21 pasivo corriente). Con otro plan de cuentas hay que
              indicarlo acá, o la cuenta se muestra como no corriente.
            </p>
          </div>
        )}
        {/* Mapeo SII */}
        <div className="relative">
          <label className="block text-xs font-medium text-content-muted mb-1">Cuenta SII (Contabilidad Electrónica)</label>
          {siiCode ? (
            <div className="flex items-center gap-2">
              <span className="flex-1 text-sm text-content border border-divider rounded-lg px-3 py-2 bg-surface-muted truncate">
                {selectedSiiName}
              </span>
              <button
                type="button"
                onClick={() => { setSiiCode(''); setSiiSearch('') }}
                className="text-xs text-danger-solid hover:text-danger-content px-2 py-1"
              >
                Quitar
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
                <input aria-label="Buscar por código o nombre SII"
                  value={siiSearch}
                  onChange={e => { setSiiSearch(e.target.value); setShowSiiDropdown(true) }}
                  onFocus={() => setShowSiiDropdown(true)}
                  placeholder="Buscar por código o nombre SII..."
                  className="w-full border border-divider rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                />
              </div>
              {showSiiDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-surface border border-divider rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredSii.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-content-subtle">Sin resultados</div>
                  ) : (
                    filteredSii.map(sii => (
                      <button
                        key={sii.id}
                        type="button"
                        onClick={() => {
                          setSiiCode(sii.code)
                          setSiiSearch('')
                          setShowSiiDropdown(false)
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 flex gap-2"
                      >
                        <span className="font-mono text-xs text-content-muted whitespace-nowrap">{sii.code}</span>
                        <span className="text-content truncate">{sii.name}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div>
          <label htmlFor="accountstab-cuenta-padre" className="block text-xs font-medium text-content-muted mb-1">Cuenta Padre</label>
          <EntityAutocomplete id="accountstab-cuenta-padre"
            options={groupAccounts.map(a => ({ id: a.id, label: `${a.code} — ${a.name}` }))}
            value={parent}
            onChange={setParent}
            placeholder="Sin cuenta padre"
            allowClear
            clearLabel="Sin cuenta padre"
          />
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <button
            type="button"
            onClick={() => setIsImputable(p => !p)}
            className="text-content-muted hover:text-primary"
          >
            {isImputable ? <ToggleRight size={22} className="text-primary" /> : <ToggleLeft size={22} />}
          </button>
          <span className="text-sm text-content">
            {isImputable ? 'Cuenta Imputable (acepta movimientos)' : 'Cuenta de Agrupación (solo jerarquía)'}
          </span>
        </label>
        {error && (
          <div className="bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">
            {error}
          </div>
        )}
      </div>
    </Modal>
  )
}

function AccountActionButtons({ account }: { account: ChartOfAccount }) {
  const deleteMut = useDeleteAccount(account.id)
  const restoreMut = useRestoreAccount(account.id)
  const [confirmDelete, setConfirmDelete] = useState<'soft' | 'hard' | null>(null)

  if (confirmDelete) {
    const isPermanent = confirmDelete === 'hard'
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs text-danger-content-soft mr-1">
          {isPermanent ? 'Eliminar permanente?' : 'Desactivar?'}
        </span>
        <button
          onClick={() => {
            deleteMut.mutate(
              isPermanent ? { permanent: true } : undefined,
              { onSettled: () => setConfirmDelete(null) },
            )
          }}
          disabled={deleteMut.isPending}
          className="px-2 py-1 text-xs text-white bg-danger-fill rounded hover:bg-danger-fill-hover disabled:opacity-50"
        >
          {deleteMut.isPending ? <Loader2 size={10} className="animate-spin" /> : 'Sí'}
        </button>
        <button
          onClick={() => setConfirmDelete(null)}
          className="px-2 py-1 text-xs text-content-muted border border-divider rounded hover:bg-soft"
        >
          No
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {!account.is_active && (
        <button
          onClick={() => restoreMut.mutate()}
          disabled={restoreMut.isPending}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-success-content-soft border border-success-border rounded-lg hover:bg-success-surface disabled:opacity-50"
          title="Reactivar cuenta"
        >
          {restoreMut.isPending ? <Loader2 size={10} className="animate-spin" /> : <RotateCcw size={10} />}
          Restaurar
        </button>
      )}
      {account.is_active && (
        <button
          onClick={() => setConfirmDelete('soft')}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-warning-content-soft border border-warning-border rounded-lg hover:bg-warning-surface"
          title="Desactivar cuenta"
        >
          <Trash2 size={10} />Desactivar
        </button>
      )}
      {!account.is_active && (
        <button
          onClick={() => setConfirmDelete('hard')}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface"
          title="Eliminar permanentemente"
        >
          <Trash2 size={10} />Eliminar
        </button>
      )}
    </div>
  )
}

export function AccountsTab() {
  // Una cuenta sin `account_currency` está en la moneda base del tenant.
  const monedaBase = useTenantCurrency()
  const [filterActive, setFilterActive] = useState('')
  const [filterType, setFilterType] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<ChartOfAccount | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const importMutation = useImportAccountsCSV()

  const params: Record<string, string> = {}
  if (filterActive) params.is_active = filterActive
  if (filterType) params.account_type = filterType

  const { data, isLoading } = useAccounts(params as Parameters<typeof useAccounts>[0])
  const { data: siiData } = useSIIAccounts()

  const accounts = data?.accounts ?? []
  const siiAccounts = siiData?.accounts ?? []
  const { can } = usePermission()
  const canCreate = can('accounting.accounts', 'create')

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2 flex-wrap">
          <select aria-label="Todas (activas e inactivas)"
            value={filterActive}
            onChange={e => setFilterActive(e.target.value)}
            className="border border-divider rounded-lg px-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary"
          >
            <option value="">Todas (activas e inactivas)</option>
            <option value="true">Solo Activas</option>
            <option value="false">Solo Inactivas</option>
          </select>
          <select aria-label="Todos los tipos"
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="border border-divider rounded-lg px-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary"
          >
            <option value="">Todos los tipos</option>
            {Object.entries(ACCOUNT_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <button
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-content border border-divider rounded-lg hover:bg-soft"
            >
              <Upload size={14} />Importar CSV
            </button>
            <button
              onClick={() => { setEditing(null); setModalOpen(true) }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover"
            >
              <Plus size={14} />Nueva Cuenta
            </button>
          </div>
        )}
      </div>

      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-content-subtle">
            <Loader2 size={20} className="animate-spin mr-2" />Cargando...
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen size={32} className="mx-auto text-content-subtle mb-3" />
            <p className="text-content-muted text-sm font-medium">Sin cuentas configuradas</p>
            {canCreate && (
              <button
                onClick={() => { setEditing(null); setModalOpen(true) }}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm text-primary border border-primary-200 rounded-lg hover:bg-primary-50"
              >
                <Plus size={14} />Crear primera cuenta
              </button>
            )}
          </div>
        ) : (
          <DataTable
            columns={[
              { key: 'code', header: 'Código', render: acc => <span className="font-mono text-content">{acc.code}</span> },
              {
                key: 'name', header: 'Nombre',
                render: acc => (
                  <>
                    {acc.parent_code && <span className="text-xs text-content-subtle mr-1">&#8627;</span>}
                    {acc.name}
                  </>
                ),
              },
              {
                key: 'description', header: 'Descripción',
                render: acc => (
                  <span className="block max-w-[200px] truncate" title={acc.description}>
                    {acc.description || <span className="text-content-subtle">—</span>}
                  </span>
                ),
              },
              {
                key: 'account_type', header: 'Tipo',
                render: acc => (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ACCOUNT_TYPE_COLORS[acc.account_type]}`}>
                    {ACCOUNT_TYPE_LABELS[acc.account_type]}
                  </span>
                ),
              },
              {
                key: 'sii_code', header: 'Cuenta SII',
                render: acc => acc.sii_code ? (
                  <span className="font-mono text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded" title={acc.sii_account_name || ''}>
                    {acc.sii_code}
                  </span>
                ) : (
                  <span className="text-content-subtle">—</span>
                ),
              },
              { key: 'parent_code', header: 'Padre', render: acc => <span className="font-mono">{acc.parent_code ?? '—'}</span> },
              {
                key: 'is_imputable', header: 'Imputable',
                render: acc => acc.is_imputable
                  ? <span className="text-success-content-soft font-medium">Sí</span>
                  : <span className="text-content-subtle">Agrupación</span>,
              },
              {
                key: 'account_currency', header: 'Moneda',
                render: acc => acc.account_currency ? (
                  <span className="font-mono text-xs px-1.5 py-0.5 bg-warning-surface text-warning-content rounded border border-warning-border">
                    {acc.account_currency}
                  </span>
                ) : (
                  /* Vacío = la moneda BASE del tenant, que no siempre es CLP:
                     un tenant peruano lleva su contabilidad en PEN. */
                  <span className="text-content-subtle text-xs">{monedaBase}</span>
                ),
              },
              {
                key: 'is_active', header: 'Estado',
                render: acc => (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    acc.is_active ? 'bg-success-surface text-success-content' : 'bg-surface-muted text-content-muted'
                  }`}>
                    {acc.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                ),
              },
              {
                key: 'acciones', header: 'Acciones',
                render: acc => (
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => { setEditing(acc); setModalOpen(true) }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft"
                    >
                      <Pencil size={10} />Editar
                    </button>
                    <AccountActionButtons account={acc} />
                  </div>
                ),
              },
            ]}
            rows={accounts}
            rowKey={acc => acc.id}
            // El panel lo pone el contenedor, que envuelve también los otros estados.
            surface={false}
            minWidth={1250}
            caption="Plan de cuentas"
          />
        )}
      </div>

      {modalOpen && (
        <AccountForm
          account={editing}
          accounts={accounts}
          siiAccounts={siiAccounts}
          onClose={() => { setModalOpen(false); setEditing(null) }}
        />
      )}

      <ImportCSVMapperModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Importar Plan de Cuentas"
        description="Suba un archivo CSV con las cuentas contables. Se realizará upsert por código: si la cuenta ya existe se actualiza, si no se crea. Las cuentas padre deben tener un código menor que sus hijas. Tipos válidos: activo, pasivo, patrimonio, ingreso, gasto, otro."
        systemFields={ACCOUNT_FIELDS}
        templateFileName="plantilla_plan_cuentas.csv"
        onImport={(file, mapping) => importMutation.mutateAsync({ file, mapping })}
        isPending={importMutation.isPending}
      />
    </div>
  )
}
